import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  MapPinIcon,
  ChevronRightIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import {
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  format,
  isPast,
  parseISO,
  startOfDay,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { AuthContext } from '../../App';
import apiClient from '../../utils/apiClient';

const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const Icon = FaDog as React.ComponentType<{ className?: string }>;
  return <Icon className={className} />;
};

interface DogDocument { docId: number; filename: string; created_at: string; }

interface Dog {
  dog_id: number;
  hundename: string;
  vollstaendiger_name?: string;
  rasse?: string;
  geburtstag?: string;
  chipnummer?: string;
  tassonummer?: string;
  geschlecht?: string;
  image_path?: string;
  image_path_updated?: number;
  haftpflicht?: string;
  insurance_name?: string;
  valid_insurance?: boolean;
  valid_tollwut?: boolean;
  tollwut_end_date?: string | null;
  documents?: { insurance?: DogDocument[]; tollwut?: DogDocument[]; };
}

interface UserEvent {
  id: string;
  title: string;
  start: string;
  start_time: string;
  end_time?: string;
  location?: string;
  participantCount?: number;
  max_teilnehmer?: number;
  creator_name?: string;
  farbe?: string;
}

interface DogCheck {
  insuranceOk: boolean;
  tollwutOk: boolean;
  tollwutExpired: boolean;
  chipOk: boolean;
  tassoOk: boolean;
  score: number;
  hasCritical: boolean;
}

interface CockpitTask {
  id: string;
  kind: 'dog' | 'finance';
  severity: 'critical' | 'warning';
  dogId?: number;
  dogName?: string;
  title: string;
  description: string;
  daysOverdue?: number;
  daysRemaining?: number;
  cta: string;
  route: string;
}

const checkDog = (dog: Dog): DogCheck => {
  const insuranceOk = !!dog.valid_insurance;
  const tollwutEndExpired = !!(dog.tollwut_end_date && (() => {
    try { return isPast(parseISO(dog.tollwut_end_date!)); } catch { return false; }
  })());
  const tollwutOk = !!dog.valid_tollwut && !tollwutEndExpired;
  const score = (insuranceOk ? 50 : 0) + (tollwutOk ? 50 : 0);
  return {
    insuranceOk,
    tollwutOk,
    tollwutExpired: tollwutEndExpired,
    chipOk: true,
    tassoOk: true,
    score,
    hasCritical: tollwutEndExpired || !insuranceOk,
  };
};

interface DrinkInfo { count: number; total: number; }

const buildTasks = (dogs: Dog[], drinks: DrinkInfo | null): CockpitTask[] => {
  const tasks: CockpitTask[] = [];
  for (const dog of dogs) {
    const c = checkDog(dog);
    if (c.tollwutExpired && dog.tollwut_end_date) {
      const days = differenceInDays(new Date(), parseISO(dog.tollwut_end_date));
      tasks.push({
        id: `tollwut-${dog.dog_id}`,
        kind: 'dog',
        severity: 'critical',
        dogId: dog.dog_id,
        dogName: dog.hundename,
        title: `${dog.hundename} · Tollwut auffrischen`,
        description: 'Trainingssperre aktiv · Tierarzt-Termin nötig',
        daysOverdue: days,
        cta: 'Bescheinigung hochladen',
        route: '/my-dogs',
      });
    } else if (!c.tollwutOk) {
      tasks.push({
        id: `tollwut-missing-${dog.dog_id}`,
        kind: 'dog',
        severity: 'critical',
        dogId: dog.dog_id,
        dogName: dog.hundename,
        title: `${dog.hundename} · Tollwut-Nachweis fehlt`,
        description: 'Pflicht für die Teilnahme am Übungsbetrieb',
        cta: 'Hochladen',
        route: '/my-dogs',
      });
    }
    if (!c.insuranceOk) {
      tasks.push({
        id: `vers-${dog.dog_id}`,
        kind: 'dog',
        severity: 'critical',
        dogId: dog.dog_id,
        dogName: dog.hundename,
        title: `${dog.hundename} · Haftpflicht-Versicherung`,
        description: 'Vereinspflicht — Nachweis fehlt',
        cta: 'Hochladen',
        route: '/my-dogs',
      });
    }
  }
  if (drinks && drinks.count > 0) {
    tasks.push({
      id: 'drinks-unpaid',
      kind: 'finance',
      severity: 'warning',
      title: 'Offene Verzehrkosten',
      description: `${drinks.count} unbezahlt${drinks.count === 1 ? 'e Position' : 'e Positionen'} · ${drinks.total.toFixed(2)} €`,
      cta: 'Bezahlen',
      route: '/my-drinks',
    });
  }
  // Critical zuerst
  return tasks.sort((a, b) => (a.severity === 'critical' ? -1 : 1) - (b.severity === 'critical' ? -1 : 1));
};

const DogCockpit: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { user, BASE_URL } = useContext(AuthContext);
  const navigate = useNavigate();
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [events, setEvents] = useState<UserEvent[]>([]);
  const [drinks, setDrinks] = useState<DrinkInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [dogRes, eventRes, drinkRes] = await Promise.all([
          apiClient.get('/api/members/dog_issues').catch(() => null),
          apiClient.get('/api/calendar/user-events/unknown').catch(() => null),
          apiClient.get('/api/drinks/mydrinks').catch(() => null),
        ]);
        if (cancelled) return;
        if (dogRes?.data?.success) setDogs(dogRes.data.data || []);
        if (eventRes?.data?.eventsBooked) setEvents(eventRes.data.eventsBooked || []);
        if (drinkRes && Array.isArray(drinkRes.data)) {
          const unpaid = drinkRes.data.filter((d: any) => d.paid === 0);
          if (unpaid.length > 0) {
            const total = unpaid.reduce((sum: number, d: any) => sum + parseFloat(d.amount || 0), 0);
            setDrinks({ count: unpaid.length, total });
          } else {
            setDrinks(null);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const tasks = useMemo(() => buildTasks(dogs, drinks), [dogs, drinks]);
  const criticalCount = tasks.filter(t => t.severity === 'critical').length;
  const warningCount = tasks.filter(t => t.severity === 'warning').length;

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return [...events]
      .map(e => {
        try {
          return { ...e, _date: parseISO(`${e.start}T${e.start_time || '00:00'}`) };
        } catch { return null; }
      })
      .filter((e): e is UserEvent & { _date: Date } => !!e && e._date >= now)
      .sort((a, b) => a._date.getTime() - b._date.getTime());
  }, [events]);

  const nextEvent = upcomingEvents[0] || null;

  // ── Empty / Loading States ──────────────────────────────────
  if (loading) {
    return (
      <div className={`bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-5 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        </div>
      </div>
    );
  }

  if (dogs.length === 0) {
    return (
      <div className={`bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-5 text-center ${className}`}>
        <div className="w-14 h-14 mx-auto rounded-2xl bg-gray-100 dark:bg-gray-800 grid place-items-center mb-3">
          <DogIcon className="w-7 h-7 text-gray-400" />
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">Noch keine Hunde registriert</p>
        <button
          onClick={() => navigate('/my-dogs')}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold"
        >
          Hund hinzufügen
        </button>
      </div>
    );
  }

  const hasCritical = dogs.some(d => checkDog(d).hasCritical);
  const dogChecks = dogs.map(d => ({ dog: d, check: checkDog(d) }));
  const totalScore = Math.round(dogChecks.reduce((s, x) => s + x.check.score, 0) / dogs.length);

  return (
    <div className={`bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden ${className}`}>
      {/* ── Header ───────────────────────────── */}
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700/60">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700/40 grid place-items-center">
            <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9"/>
              <path d="M14.5 9.5l-2 5-5 2 2-5z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary-600 dark:text-primary-400 leading-none">
              Cockpit
            </p>
            <h2 className="text-sm sm:text-base font-extrabold text-gray-900 dark:text-white leading-tight mt-0.5">
              {dogs.length} {dogs.length === 1 ? 'Hund' : 'Hunde'} · {totalScore}% komplett
            </h2>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-[10px]">
          {criticalCount > 0 && (
            <span className="px-2 py-1 rounded-md bg-error-500/10 border border-error-500/40 text-error-600 dark:text-error-400 font-bold">
              {criticalCount} critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="px-2 py-1 rounded-md bg-warning-500/10 border border-warning-500/40 text-warning-600 dark:text-warning-500 font-bold">
              {warningCount} offen
            </span>
          )}
          {criticalCount === 0 && warningCount === 0 && (
            <span className="px-2 py-1 rounded-md bg-primary-500/10 border border-primary-500/30 text-primary-600 dark:text-primary-400 font-bold">
              alles ok
            </span>
          )}
        </div>
      </div>

      {/* ── Critical Alert Banner ─────────────── */}
      {hasCritical && (
        <div className="m-3 sm:m-4 rounded-xl border border-error-500/60 bg-error-500/5 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-1 bg-error-500"></div>
          <div className="flex items-start gap-3 p-3 sm:p-4 pl-4 sm:pl-5">
            <div className="w-10 h-10 rounded-xl bg-error-500/15 border border-error-500/50 grid place-items-center flex-shrink-0">
              <ExclamationTriangleIcon className="w-5 h-5 text-error-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-error-500 text-white tracking-wider uppercase">Critical</span>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-error-600 dark:text-error-400">Aktion erforderlich</p>
              </div>
              <h3 className="text-sm sm:text-base font-extrabold text-gray-900 dark:text-white">
                {criticalCount === 1
                  ? `${tasks.find(t => t.severity === 'critical')?.title}`
                  : `${criticalCount} kritische Aufgaben offen`}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                Trainingsteilnahme nicht möglich, bis die Pflichtnachweise vorliegen.
              </p>
            </div>
            <button
              onClick={() => navigate('/my-dogs')}
              className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-lg bg-error-500 hover:bg-error-600 text-white text-xs font-bold flex-shrink-0"
            >
              Beheben
            </button>
          </div>
        </div>
      )}

      {/* ── Hauptraster ──────────────────────── */}
      <div className="p-3 sm:p-4 grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
        {/* Hunde-Zone */}
        <div className={`${dogs.length >= 3 ? 'lg:col-span-7' : 'lg:col-span-5'} rounded-xl bg-gray-50/60 dark:bg-[#131a25] border border-gray-200 dark:border-[#2c3a52] p-3 sm:p-4`}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary-600 dark:text-primary-400">
              Meine Hunde
            </p>
            <button
              onClick={() => navigate('/my-dogs')}
              className="text-[10px] font-bold text-primary-600 dark:text-primary-400 hover:underline"
            >
              Verwalten →
            </button>
          </div>

          <div className={`grid gap-2 sm:gap-3 ${
            dogs.length === 1 ? 'grid-cols-1' :
            dogs.length === 2 ? 'grid-cols-1' :
            'grid-cols-2 sm:grid-cols-3'
          }`}>
            {dogChecks.map(({ dog, check }) => (
              <DogCard
                key={dog.dog_id}
                dog={dog}
                check={check}
                BASE_URL={BASE_URL}
                compact={dogs.length >= 3}
                onClick={() => navigate('/my-dogs')}
              />
            ))}
          </div>

        </div>

        {/* Aufgaben + Training Stack */}
        <div className={`${dogs.length >= 3 ? 'lg:col-span-5' : 'lg:col-span-7'} flex flex-col gap-3`}>
          {/* Aufgaben-Queue */}
          <div className="rounded-xl bg-gray-50/60 dark:bg-[#131a25] border border-gray-200 dark:border-[#2c3a52] p-3 sm:p-4 flex-1">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-warning-600 dark:text-warning-500">
                Aufgaben · {tasks.length} offen
              </p>
              {tasks.length === 0 && (
                <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400">✓ alles erledigt</span>
              )}
            </div>

            {tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-3 text-center">
                <div className="w-10 h-10 rounded-xl bg-primary-500/15 border border-primary-500/30 grid place-items-center mb-2">
                  <CheckIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" strokeWidth={3} />
                </div>
                <p className="text-xs font-bold text-gray-900 dark:text-white">Alles erledigt</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">Deine Hunde sind startklar</p>
              </div>
            ) : (
              <div className="space-y-2">
                {tasks.slice(0, 3).map(task => (
                  <TaskRow key={task.id} task={task} onClick={() => navigate(task.route)} />
                ))}
                {tasks.length > 3 && (
                  <button
                    onClick={() => navigate('/my-dogs')}
                    className="w-full text-center text-[10px] font-bold text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 py-1"
                  >
                    +{tasks.length - 3} weitere Aufgaben →
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Nächstes Training */}
          {nextEvent && (
            <NextTrainingCard event={nextEvent} onClick={() => navigate('/calendar')} />
          )}
        </div>
      </div>

    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// DogCard — Dog mit Activity-Ring (Apple Watch Style)
// ─────────────────────────────────────────────────────────────
interface DogCardProps {
  dog: Dog;
  check: DogCheck;
  BASE_URL: string;
  compact?: boolean;
  onClick: () => void;
}

const DogCard: React.FC<DogCardProps> = ({ dog, check, BASE_URL, compact, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const photoUrl = dog.image_path && !imageError
    ? `${BASE_URL}/api/dogs/image-url/${dog.dog_id}?token=${localStorage.getItem('jwt_token')}&ts=${dog.image_path_updated || 0}`
    : null;

  const ageStr = (() => {
    if (!dog.geburtstag) return null;
    try {
      const birth = parseISO(dog.geburtstag);
      const years = differenceInYears(new Date(), birth);
      const months = differenceInMonths(new Date(), birth) % 12;
      return `${years}J${months > 0 ? ` ${months}M` : ''}`;
    } catch { return null; }
  })();

  // Ring color based on score
  const ringColor = check.hasCritical ? '#ef4444' : check.score < 100 ? '#f59e0b' : '#16a34a';
  const borderColor = check.hasCritical ? '!border-error-500/60' : check.score < 100 ? '!border-warning-500/40' : 'hover:border-primary-500/40';
  const bgColor = check.hasCritical ? 'bg-error-500/5' : '';
  const circumference = 2 * Math.PI * 44;
  const offset = circumference * (1 - check.score / 100);

  // Indicator dot
  const indicator = check.hasCritical
    ? { bg: 'bg-error-500', icon: <XMarkIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} /> }
    : check.score < 100
      ? { bg: 'bg-warning-500', icon: <ExclamationTriangleIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} /> }
      : { bg: 'bg-primary-500', icon: <CheckIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} /> };

  return (
    <button
      onClick={onClick}
      className={`text-left w-full rounded-xl p-2.5 sm:p-3 border border-gray-200 dark:border-[#2c3a52] bg-white dark:bg-[#1c2432] transition hover:shadow-sm ${borderColor} ${bgColor}`}
    >
      <div className={`flex ${compact ? 'flex-col items-center text-center' : 'items-center'} gap-2 sm:gap-3 mb-2`}>
        <div className="relative flex-shrink-0">
          <svg className={`${compact ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-14 h-14'} -rotate-90`} viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="44" stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeWidth="8" fill="none"/>
            <circle
              cx="50" cy="50" r="44"
              stroke={ringColor}
              strokeWidth="8"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-1.5 rounded-full overflow-hidden border-2 border-white dark:border-[#1c2432] bg-gray-100 dark:bg-gray-800">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={dog.hundename}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full grid place-items-center">
                <DogIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>
          <div className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full ${indicator.bg} border-2 border-white dark:border-[#1c2432] grid place-items-center`}>
            {indicator.icon}
          </div>
        </div>

        <div className={`min-w-0 ${compact ? '' : 'flex-1'}`}>
          <div className="text-xs sm:text-sm font-extrabold text-gray-900 dark:text-white truncate">
            {dog.hundename}
          </div>
          <div className="text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400 truncate">
            {[dog.rasse, ageStr].filter(Boolean).join(' · ') || '—'}
          </div>
          <div className={`text-[9px] sm:text-[10px] font-bold ${check.hasCritical ? 'text-error-500' : check.score < 100 ? 'text-warning-600 dark:text-warning-500' : 'text-primary-600 dark:text-primary-400'}`}>
            {check.score}%
          </div>
        </div>
      </div>

      {/* Compliance-Mini-Pills */}
      <div className="grid grid-cols-2 gap-1">
        <Pill ok={check.insuranceOk} label="Vers" />
        <Pill ok={check.tollwutOk} label="Tollwut" critical={check.tollwutExpired} />
      </div>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────
// Pill
// ─────────────────────────────────────────────────────────────
const Pill: React.FC<{ ok: boolean; label: string; critical?: boolean }> = ({ ok, label, critical }) => {
  if (ok) {
    return (
      <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] font-bold bg-primary-500/15 border border-primary-500/30 text-primary-700 dark:text-primary-300">
        {label} ✓
      </span>
    );
  }
  if (critical) {
    return (
      <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] font-bold bg-error-500/20 border border-error-500/60 text-error-600 dark:text-error-400">
        {label} ✗
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] font-bold bg-warning-500/20 border border-warning-500/50 text-warning-600 dark:text-warning-500">
      {label} !
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// TaskRow
// ─────────────────────────────────────────────────────────────
const TaskRow: React.FC<{ task: CockpitTask; onClick: () => void }> = ({ task, onClick }) => {
  const isCritical = task.severity === 'critical';
  const isFinance = task.kind === 'finance';
  const Icon = isFinance ? ShoppingCartIcon : ExclamationTriangleIcon;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-lg p-2.5 border flex items-start gap-2.5 transition hover:bg-white dark:hover:bg-[#1c2432] ${
        isCritical
          ? 'border-error-500/50 bg-error-500/5'
          : 'border-warning-500/40 bg-warning-500/5'
      }`}
    >
      <div className={`w-7 h-7 rounded-lg grid place-items-center flex-shrink-0 ${
        isCritical ? 'bg-error-500/20 border border-error-500/50' : 'bg-warning-500/15 border border-warning-500/40'
      }`}>
        <Icon className={`w-3.5 h-3.5 ${isCritical ? 'text-error-500' : 'text-warning-600 dark:text-warning-500'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <span className="text-xs font-extrabold text-gray-900 dark:text-white truncate">{task.title}</span>
          {isCritical && (
            <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-error-500 text-white tracking-wider">SOFORT</span>
          )}
        </div>
        <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">{task.description}</div>
        {task.daysOverdue !== undefined && (
          <div className="text-[10px] font-bold text-error-600 dark:text-error-400">
            seit {task.daysOverdue} Tagen abgelaufen
          </div>
        )}
      </div>
      <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
    </button>
  );
};

// ─────────────────────────────────────────────────────────────
// NextTrainingCard
// ─────────────────────────────────────────────────────────────
const NextTrainingCard: React.FC<{ event: UserEvent & { _date?: Date }; onClick: () => void }> = ({ event, onClick }) => {
  const date = event._date || (() => {
    try { return parseISO(`${event.start}T${event.start_time || '00:00'}`); } catch { return new Date(); }
  })();
  const days = differenceInDays(startOfDay(date), startOfDay(new Date()));
  const relLabel = days === 0 ? 'Heute' : days === 1 ? 'Morgen' : `in ${days} Tagen`;
  const cap = event.max_teilnehmer && event.max_teilnehmer > 0
    ? Math.min(100, Math.round(((event.participantCount || 0) / event.max_teilnehmer) * 100))
    : null;

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl bg-gray-50/60 dark:bg-[#131a25] border border-gray-200 dark:border-[#2c3a52] p-3 sm:p-4 hover:bg-white dark:hover:bg-[#1c2432] transition"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary-600 dark:text-primary-400">
          Nächstes Training
        </p>
        <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wider">
          {relLabel}
        </span>
      </div>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-[#2c3a52] flex flex-col items-center justify-center">
          <div className="text-[8px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {format(date, 'EE', { locale: de })}
          </div>
          <div className="text-xl font-extrabold text-gray-900 dark:text-white leading-none">
            {format(date, 'dd')}
          </div>
          <div className="text-[8px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {format(date, 'MMM', { locale: de })}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-extrabold text-gray-900 dark:text-white truncate">{event.title}</h4>
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            <span className="inline-flex items-center gap-1">
              <ClockIcon className="w-3 h-3" />
              {event.start_time?.slice(0, 5)}{event.end_time ? `–${event.end_time.slice(0, 5)}` : ''}
            </span>
            {event.location && (
              <span className="inline-flex items-center gap-1">
                <MapPinIcon className="w-3 h-3" />
                {event.location}
              </span>
            )}
          </div>
          {cap !== null && (
            <div className="mt-2">
              <div className="flex justify-between items-center text-[10px] mb-1">
                <span className="text-gray-500 dark:text-gray-400">Plätze</span>
                <span className="font-bold text-gray-900 dark:text-white tabular-nums">
                  {event.participantCount}/{event.max_teilnehmer}
                </span>
              </div>
              <div className="h-1 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
                <div className="h-full bg-primary-500" style={{ width: `${cap}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

export default DogCockpit;
